import React, { useState, useEffect } from 'react';
import { holidayApi } from '../../services/api';
import { Holiday, HolidayFormData, HolidayType } from '../../types';
import { CalendarIcon, PlusIcon, PencilIcon, TrashIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';

const HolidayList: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [importing, setImporting] = useState(false);

  // Lists for dropdowns
  const [availableEstados, setAvailableEstados] = useState<string[]>([]);
  const [availableCidades, setAvailableCidades] = useState<string[]>([]);

  // Filters
  const [filterTipo, setFilterTipo] = useState<HolidayType | ''>('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterCidade, setFilterCidade] = useState('');
  const [filterAno, setFilterAno] = useState(new Date().getFullYear());
  const [filterAtivo, setFilterAtivo] = useState<boolean | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState<HolidayFormData>({
    data: '',
    nome: '',
    tipo: 'nacional',
    estado: '',
    cidade: '',
    ativo: true
  });

  // Available cities for selected state in form
  const [formCidades, setFormCidades] = useState<string[]>([]);

  useEffect(() => {
    loadHolidays();
  }, [filterTipo, filterEstado, filterCidade, filterAno, filterAtivo]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterTipo) params.tipo = filterTipo;
      if (filterEstado) params.estado = filterEstado;
      if (filterCidade) params.cidade = filterCidade;
      if (filterAno) params.ano = filterAno;
      if (filterAtivo !== undefined) params.ativo = filterAtivo;

      const data = await holidayApi.getHolidays(params);
      const holidaysArray = Array.isArray(data) ? data : [];
      setHolidays(holidaysArray);

      // Extract unique estados and cidades
      const estados = new Set<string>();
      const cidades = new Set<string>();

      holidaysArray.forEach(h => {
        if (h.estado) estados.add(h.estado);
        if (h.cidade) cidades.add(h.cidade);
      });

      setAvailableEstados(Array.from(estados).sort());
      setAvailableCidades(Array.from(cidades).sort());
    } catch (err) {
      setError('Erro ao carregar feriados');
      console.error(err);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    const ano = prompt('Digite o ano para importar (ex: 2025):');
    if (!ano) return;

    const importarTodos = window.confirm(
      'Deseja importar feriados de TODOS os estados do Brasil?\n\n' +
      'Clique em OK para importar todos os estados.\n' +
      'Clique em Cancelar para escolher um estado específico.'
    );

    try {
      setImporting(true);

      if (importarTodos) {
        // Importar todos os estados
        const estados = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
        let totalImportados = 0;
        let totalAtualizados = 0;
        let erros: string[] = [];

        for (const estado of estados) {
          try {
            const result = await holidayApi.importHolidays(parseInt(ano), estado);
            totalImportados += result.importados;
            totalAtualizados += result.atualizados;
            erros = erros.concat(result.erros);
          } catch (err) {
            erros.push(`Erro ao importar ${estado}`);
          }
        }

        alert(
          `Importacao concluida para todos os estados!\n\n` +
          `Importados: ${totalImportados}\n` +
          `Atualizados: ${totalAtualizados}` +
          (erros.length > 0 ? `\n\nErros: ${erros.slice(0, 5).join(', ')}${erros.length > 5 ? '...' : ''}` : '')
        );
      } else {
        // Importar estado específico
        const estado = prompt('Digite a sigla do estado (ex: SP):');
        if (!estado) {
          setImporting(false);
          return;
        }

        const result = await holidayApi.importHolidays(parseInt(ano), estado.trim().toUpperCase());
        alert(
          `Importacao concluida!\n` +
          `Importados: ${result.importados}\n` +
          `Atualizados: ${result.atualizados}` +
          (result.erros.length > 0 ? `\nErros: ${result.erros.join(', ')}` : '')
        );
      }

      loadHolidays();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao importar feriados');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id: number, nome: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir o feriado "${nome}"?`)) {
      return;
    }

    try {
      await holidayApi.deleteHoliday(id);
      setHolidays(prev => prev.filter(h => h.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erro ao excluir feriado');
    }
  };

  const handleToggleActive = async (holiday: Holiday) => {
    try {
      const updated = await holidayApi.toggleActive(holiday.id);
      setHolidays(prev => prev.map(h => h.id === updated.id ? updated : h));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const openModal = (holiday?: Holiday) => {
    if (holiday) {
      setEditingHoliday(holiday);
      setFormData({
        data: holiday.data,
        nome: holiday.nome,
        tipo: holiday.tipo,
        estado: holiday.estado || '',
        cidade: holiday.cidade || '',
        ativo: holiday.ativo
      });

      // Load cities for this state
      if (holiday.estado) {
        updateFormCidades(holiday.estado);
      }
    } else {
      setEditingHoliday(null);
      setFormData({
        data: '',
        nome: '',
        tipo: 'nacional',
        estado: '',
        cidade: '',
        ativo: true
      });
      setFormCidades([]);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
  };

  const updateFormCidades = (estado: string) => {
    if (!estado) {
      setFormCidades([]);
      return;
    }

    // Get cities that already have holidays in this state
    const cidadesDoEstado = holidays
      .filter(h => h.estado === estado && h.cidade)
      .map(h => h.cidade!)
      .filter((v, i, a) => a.indexOf(v) === i) // unique
      .sort();

    setFormCidades(cidadesDoEstado);
  };

  const handleFormEstadoChange = (estado: string) => {
    setFormData({ ...formData, estado, cidade: '' });
    updateFormCidades(estado);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prepare data
    const submitData = {
      ...formData,
      estado: formData.estado || undefined,
      cidade: formData.cidade || undefined
    };

    try {
      if (editingHoliday) {
        const updated = await holidayApi.updateHoliday(editingHoliday.id, submitData);
        setHolidays(prev => prev.map(h => h.id === updated.id ? updated : h));
      } else {
        const created = await holidayApi.createHoliday(submitData);
        setHolidays(prev => [...prev, created]);
      }
      closeModal();
      loadHolidays(); // Reload to update filters
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erro ao salvar feriado');
    }
  };

  const filteredHolidays = holidays.filter(holiday =>
    holiday.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get unique cities for filter based on selected state
  const filterCidadeOptions = filterEstado
    ? holidays
        .filter(h => h.estado === filterEstado && h.cidade)
        .map(h => h.cidade!)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
    : availableCidades;

  const tipoLabels: Record<HolidayType, string> = {
    nacional: 'Nacional',
    estadual: 'Estadual',
    municipal: 'Municipal',
    facultativo: 'Facultativo'
  };

  const tipoColors: Record<HolidayType, string> = {
    nacional: 'bg-red-100 text-red-800',
    estadual: 'bg-blue-100 text-blue-800',
    municipal: 'bg-green-100 text-green-800',
    facultativo: 'bg-yellow-100 text-yellow-800'
  };

  const estadosBrasil = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold text-gray-900 flex items-center">
            <CalendarIcon className="h-8 w-8 mr-2 text-purple-600" />
            Feriados
          </h1>
          <p className="text-gray-600 mt-1">Gerencie feriados nacionais, estaduais e municipais</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleImport}
            disabled={importing}
            className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
            {importing ? 'Importando...' : 'Importar da API'}
          </button>
          <button
            onClick={() => openModal()}
            className="flex items-center px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Novo Feriado
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filtros Melhorados */}
      <div className="mb-4 bg-white border border-gray-300 rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
          <CalendarIcon className="h-4 w-4 mr-1.5 text-purple-600" />
          Filtros
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          {/* Busca por nome */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Buscar Nome</label>
            <input
              type="text"
              placeholder="Nome do feriado..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* Ano */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Ano</label>
            <input
              type="number"
              value={filterAno}
              onChange={(e) => setFilterAno(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value as HolidayType | '')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Todos</option>
              <option value="nacional">Nacional</option>
              <option value="estadual">Estadual</option>
              <option value="municipal">Municipal</option>
              <option value="facultativo">Facultativo</option>
            </select>
          </div>

          {/* Estado */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Estado (UF)</label>
            <select
              value={filterEstado}
              onChange={(e) => {
                setFilterEstado(e.target.value);
                setFilterCidade(''); // Reset cidade quando mudar estado
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Todos</option>
              {availableEstados.map(est => (
                <option key={est} value={est}>{est}</option>
              ))}
            </select>
          </div>

          {/* Cidade */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Município</label>
            <select
              value={filterCidade}
              onChange={(e) => setFilterCidade(e.target.value)}
              disabled={!filterEstado}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Todos</option>
              {filterCidadeOptions.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filterAtivo === undefined ? '' : filterAtivo ? 'true' : 'false'}
              onChange={(e) => setFilterAtivo(e.target.value === '' ? undefined : e.target.value === 'true')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>

        {/* Botão Limpar */}
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => {
              setFilterTipo('');
              setFilterEstado('');
              setFilterCidade('');
              setFilterAtivo(undefined);
              setSearchTerm('');
            }}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Localizacao</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Origem</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acoes</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredHolidays.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  {holidays.length === 0
                    ? 'Nenhum feriado cadastrado. Clique em "Importar da API" ou "Novo Feriado" para começar.'
                    : 'Nenhum feriado encontrado com estes filtros.'}
                </td>
              </tr>
            ) : (
              filteredHolidays.map((holiday) => (
                <tr key={holiday.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {new Date(holiday.data + 'T00:00:00').toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{holiday.nome}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${tipoColors[holiday.tipo]}`}>
                      {tipoLabels[holiday.tipo]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {holiday.tipo === 'nacional' ? '-' :
                     holiday.tipo === 'estadual' ? holiday.estado :
                     `${holiday.cidade}, ${holiday.estado}`}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleToggleActive(holiday)}
                      className={`px-3 py-1 text-xs font-medium rounded-full ${
                        holiday.ativo
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {holiday.ativo ? 'Ativo' : 'Inativo'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500">
                    {holiday.importado_api ? 'API' : 'Manual'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(holiday)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(holiday.id, holiday.nome)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Exibindo {filteredHolidays.length} de {holidays.length} feriado(s)
      </div>

      {/* Modal Melhorado */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-gray-900">
                  {editingHoliday ? 'Editar Feriado' : 'Novo Feriado'}
                </h2>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                    <input
                      type="date"
                      value={formData.data}
                      onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => {
                        const novoTipo = e.target.value as HolidayType;
                        setFormData({ ...formData, tipo: novoTipo, estado: '', cidade: '' });
                        setFormCidades([]);
                      }}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="nacional">Nacional</option>
                      <option value="estadual">Estadual</option>
                      <option value="municipal">Municipal</option>
                      <option value="facultativo">Facultativo</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    placeholder="Ex: Natal, Carnaval, Revolucao Constitucionalista"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {(formData.tipo === 'estadual' || formData.tipo === 'municipal') && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Estado (UF) *</label>
                      <select
                        value={formData.estado}
                        onChange={(e) => handleFormEstadoChange(e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione...</option>
                        {estadosBrasil.map(uf => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>

                    {formData.tipo === 'municipal' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Municipio *</label>
                        {formCidades.length > 0 ? (
                          <div className="space-y-2">
                            <select
                              value={formData.cidade}
                              onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            >
                              <option value="">Selecione ou digite novo...</option>
                              {formCidades.map(cidade => (
                                <option key={cidade} value={cidade}>{cidade}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={formData.cidade}
                              onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                              placeholder="Ou digite um novo municipio"
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                            <p className="text-xs text-gray-500">Selecione um municipio existente acima ou digite um novo</p>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={formData.cidade}
                            onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                            placeholder="Ex: Paulinia, Campinas"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.ativo}
                      onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Feriado ativo</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
                  >
                    {editingHoliday ? 'Salvar Alteracoes' : 'Criar Feriado'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayList;
